import { financialTone as amountTone } from "./financialTone";

const unavailable = "—";

const equityBasisLabels = {
  cumulative_realized_pl: "Cumulative realized P/L from closed trades",
  account_equity_history: "Recorded account equity history",
};

/**
 * Whether the realised dollar figures open at the production baseline.
 *
 * The baseline states a frozen total for its own period, so the currency
 * figures now span the same history as the trade counts beside them and are
 * labelled plainly. What still differs is the daily and weekly windows,
 * which never receive the baseline — those trades closed months earlier —
 * and the curve, which has no per-trade record of the baseline period to
 * plot and so only sets its origin from it.
 */
function opensAtBaseline(performance) {
  return performance?.pl_scope === "baseline_plus_tracked";
}

function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : null;
}

function formatCurrency(value, { signed = false } = {}) {
  const amount = toNumber(value);
  if (amount === null) return unavailable;

  const formatted = Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = signed ? (amount >= 0 ? "+" : "-") : "";
  return `${sign}$${formatted}`;
}

function formatPercentage(value) {
  const amount = toNumber(value);
  if (amount === null) return unavailable;
  return `${amount.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;
}

/**
 * Pips, on GoldBot's own convention. The API has already signed them, so a
 * losing total arrives negative and prints that way; zero prints unsigned
 * because "+0 pips" reads like a rounded-down result rather than none.
 */
function formatPips(value) {
  const amount = toNumber(value);
  if (amount === null) return unavailable;
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${sign}${Math.abs(amount).toLocaleString("en-US", {
    maximumFractionDigits: 1,
  })} pips`;
}

/**
 * Say how much of the recorded closed history actually carries a profit
 * value, and which history it is. A closed trade with a blank result is
 * unknown P/L, not a breakeven, so it is excluded from every metric and
 * reported here instead.
 */
export function formatCoverageDetail(performance) {
  const baselineTrades = toNumber(performance?.baseline?.trades);
  const tracked = toNumber(performance?.tracked_closed_trades);

  // Where the number came from, said plainly: the stated production history
  // plus whatever has closed since.
  if (performance?.baseline?.applied && baselineTrades) {
    if (tracked) {
      return `${baselineTrades} baseline + ${tracked} tracked`;
    }
    return `${baselineTrades} baseline trades`;
  }

  const known = toNumber(performance?.closed_with_known_pl);
  const unknown = toNumber(performance?.closed_with_unknown_pl);
  if (known !== null && unknown) {
    return `${known} of ${known + unknown} with recorded P/L`;
  }
  if (performance?.production_since) return "Production history";
  return "Closed trades";
}

/**
 * Pips are only published for trades whose exit price the history can
 * recover. Where some cannot, say so rather than letting a partial sum read
 * as the whole.
 */
export function formatPipCoverageDetail(performance) {
  const unknown = toNumber(performance?.closed_with_unknown_pips);
  const known = toNumber(performance?.closed_with_known_pips);
  if (!unknown || known === null) return undefined;
  return `${known} of ${known + unknown} with a recorded exit`;
}

export function buildPerformanceMetrics(performance) {
  const available = Boolean(performance?.available);
  const fromBaseline = available && opensAtBaseline(performance);
  const closedTrades = available
    ? toNumber(performance?.total_closed_trades)
    : null;

  return [
    {
      label: "Total Trades",
      value: closedTrades === null ? unavailable : String(closedTrades),
      detail: available
        ? formatCoverageDetail(performance)
        : "No recorded history",
    },
    {
      label: "Win Rate",
      value: available ? formatPercentage(performance?.win_rate) : unavailable,
      tone: available ? amountTone(performance?.win_rate) : "",
      detail:
        available && toNumber(performance?.win_rate) !== null
          ? `${performance.wins}W · ${performance.losses}L`
          : undefined,
    },
    {
      // "Total" again, and accurately so: the baseline now states a
      // realised figure for its own period, so this spans the same history
      // as the trade count beside it.
      label: "Total Profit",
      value: available
        ? formatCurrency(performance?.total_realized_pl, { signed: true })
        : unavailable,
      tone: available ? amountTone(performance?.total_realized_pl) : "",
      detail: fromBaseline
        ? "Realized, production baseline onward"
        : "Realized, closed trades",
    },
    {
      label: "Total Pips Won",
      value: available ? formatPips(performance?.total_pips_won) : unavailable,
      tone: available ? amountTone(performance?.total_pips_won) : "",
      detail: available ? formatPipCoverageDetail(performance) : undefined,
    },
    {
      label: "Daily P/L",
      value: available
        ? formatCurrency(performance?.daily_pl, { signed: true })
        : unavailable,
      tone: available ? amountTone(performance?.daily_pl) : "",
    },
    {
      label: "Weekly P/L",
      value: available
        ? formatCurrency(performance?.weekly_pl, { signed: true })
        : unavailable,
      tone: available ? amountTone(performance?.weekly_pl) : "",
    },
    {
      label: "Monthly P/L",
      value: available
        ? formatCurrency(performance?.monthly_pl, { signed: true })
        : unavailable,
      tone: available ? amountTone(performance?.monthly_pl) : "",
    },
    {
      label: "Total Pips Lost",
      value: available ? formatPips(performance?.total_pips_lost) : unavailable,
      tone: available ? amountTone(performance?.total_pips_lost) : "",
      detail: available ? formatPipCoverageDetail(performance) : undefined,
    },
  ];
}

export function getPerformanceState({ data, error, isLoading }) {
  if (isLoading && !data) {
    return { eyebrow: "Loading performance", message: "Loading performance..." };
  }
  if (error) {
    return { eyebrow: "API unavailable", message: "Performance unavailable" };
  }
  if (!data || !data.available) {
    return {
      eyebrow: "No trade history",
      message:
        data?.unavailable_reason ||
        "No recorded GoldBot trade history is available",
    };
  }
  if (!data.total_closed_trades) {
    return { eyebrow: "No closed trades", message: null };
  }
  return {
    eyebrow: `${data.total_closed_trades} closed ${
      data.total_closed_trades === 1 ? "trade" : "trades"
    }`,
    message: null,
  };
}

/**
 * What to say when there is no curve to draw.
 *
 * "Not enough recorded trade history" would be untrue on a dashboard
 * reporting thirty closed trades: the history exists, it just carries no
 * per-trade dollar result to plot. Say that instead.
 */
export function getEquityEmptyMessage(performance) {
  if (opensAtBaseline(performance)) {
    return "No closed trades have been tracked yet. The equity curve starts from the first trade after live tracking began.";
  }
  return "Not enough recorded trade history to draw an equity curve yet.";
}

/**
 * Project the recorded curve into a 0-100 viewBox. Returns hasCurve: false
 * whenever there is not enough real history to draw an honest line.
 */
export function buildEquityCurve(performance) {
  const points = Array.isArray(performance?.equity_curve)
    ? performance.equity_curve
        .map((point) => ({
          timestamp: point?.timestamp,
          value: toNumber(point?.value),
        }))
        .filter((point) => point.value !== null)
    : [];

  if (points.length < 2) {
    return {
      hasCurve: false,
      points: [],
      line: "",
      area: "",
      basisLabel: null,
      firstLabel: null,
      lastLabel: null,
    };
  }

  const values = points.map((point) => point.value);
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const span = maximum - minimum;

  const projected = points.map((point, index) => ({
    x: Number(((index / (points.length - 1)) * 100).toFixed(2)),
    y: Number(
      (span === 0 ? 50 : 100 - ((point.value - minimum) / span) * 100).toFixed(2)
    ),
  }));

  const line = projected
    .map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`)
    .join(" ");

  // A cumulative P/L curve starts at zero, so a signed value reads correctly.
  // Recorded account equity is an absolute balance and must not be signed.
  const signed = performance?.equity_curve_basis === "cumulative_realized_pl";

  return {
    hasCurve: true,
    points: projected,
    line,
    area: `${line} L100 100 L0 100 Z`,
    // The curve plots tracked trades only — the baseline carries no
    // per-trade dollar result to draw — so it must not be captioned as the
    // whole history.
    basisLabel:
      opensAtBaseline(performance) &&
      performance?.equity_curve_basis === "cumulative_realized_pl"
        ? "Realized P/L, opening at the production baseline"
        : equityBasisLabels[performance?.equity_curve_basis] || null,
    firstLabel: formatCurrency(values[0], { signed }),
    lastLabel: formatCurrency(values[values.length - 1], { signed }),
  };
}
